/**
 * /zh-tw/decision-log-template — 決策紀錄範本 (Traditional Chinese).
 *
 * Template sibling of /zh-tw/decision-log (the concept page, currently our
 * best-ranking zh-tw URL). Targets 「決策紀錄 範本」「decision log 範本」
 * 「決策記錄表」. Gives the copyable field list up front, then the Draftlize
 * turn, mirroring DecisionLogTemplateZh.tsx in Traditional with Taiwan
 * terminology (範本/專案/欄位).
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

const FIELDS: Array<{ name: string; desc: string }> = [
  { name: '決策', desc: '決定了什麼，一句話講清楚。日後被搜尋的就是這一句。' },
  { name: '脈絡', desc: '為什麼此刻需要這個決定——當時的限制、壓力、已知條件。' },
  { name: '選項', desc: '認真考慮過哪些方案。只有一個選項的決策，多半還沒想完。' },
  { name: '理由', desc: '為什麼選這個、捨棄其他。這是幾個月後最值錢的欄位。' },
  { name: '依賴', desc: '這個決策建立在哪些前提或其他決策之上。前提變了，這裡就該亮燈。' },
  { name: '狀態', desc: '生效中／已被取代／已撤銷。沒有狀態欄的紀錄，讀的人不知道還能不能信。' },
  { name: '負責人', desc: '誰拍板。不是為了咎責，是為了日後有問題知道找誰補脈絡。' },
  { name: '日期', desc: '什麼時候定的。搭配狀態欄，讀者能判斷這個決定的「保鮮期」。' },
];

export function ZhTwDecisionLogTemplate() {
  return (
    <MarketingShell
      currentLocale="zhtw"
      hreflangPath="/decision-log-template"
      hreflangLocales={['en', 'zh', 'zhtw']}
      documentTitle="決策紀錄範本（不會過時的那一種）· Draftlize"
      documentDescription="一份可以直接抄走的決策紀錄範本——決策、脈絡、選項、理由、依賴、狀態、負責人、日期八個欄位。但要知道：寫在 Notion 或文件裡的範本，填完那一刻就開始過時。在 Draftlize 裡每個欄位都是一張結構化卡片，上游決策一改，依賴它的卡片自動標記 stale。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">範本 · 決策紀錄</span>
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 4.8vw, 60px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            決策紀錄範本——<br />
            <em>拿去用，但先知道它會過時。</em>
          </h1>
          <p className="lede">
            你要找的<em>決策紀錄範本</em>就在下面：八個欄位，貼到 Notion、Google 文件或 markdown 都能用。
            不過先說實話：範本是一種「填空格式」，填完的那一刻就是它最準確的一刻——之後每一次上游決策變動，
            它都在悄悄失真。這一頁先給你能直接抄走的範本，再告訴你怎麼讓它<em>活著</em>。
            概念層面的「決策紀錄是什麼」，見 <a href="/zh-tw/decision-log" style={{ color: 'var(--amber)' }}>決策紀錄指南</a>。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">八個欄位。<em>隨便抄到哪都行。</em></h2>
        </div>
        <div style={{ maxWidth: 820 }}>
          {FIELDS.map((f, i) => (
            <div
              key={f.name}
              style={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr',
                gap: 16,
                padding: '14px 0',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
              }}
            >
              <strong className="serif" style={{ fontSize: 17, color: 'var(--ink-0)' }}>{f.name}</strong>
              <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.desc}</p>
            </div>
          ))}
          <p style={{ fontSize: 15.5, lineHeight: 1.7, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            這個格式和 ADR（架構決策紀錄）相通——ADR 是工程決策的特化版。
            一個決策一筆紀錄，別把三個決定塞進同一格。
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">為什麼範本會<em>過時。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">範本是格式，不是系統</h3>
            <p>八個欄位能保證你「寫下來的當下」是完整的，但它不知道世界後來變了。定價策略改了、平台規則變了——紀錄裡引用舊前提的那幾行，不會自己舉手。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">「依賴」欄是一個注定失信的承諾</h3>
            <p>你寫下這個決策依賴哪些前提。之後某個前提變了，要靠某個人「記得回來翻」這份文件——沒有人記得。於是最該保鮮的欄位，最先腐爛。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">「填完」就是最高水位線</h3>
            <p>紀錄在寫完那一刻最準，之後只會越來越不準——而且你不知道哪一筆已經不準。團隊開始不信文件、改問當事人，決策紀錄就名存實亡了。</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">同一份範本，<em>變成活的。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每個欄位是一張卡片，不是一個儲存格</h3>
            <p>在 Draftlize 裡，決策、脈絡、選項各自是有 ID 的結構化卡片，能被引用、被連結——而不是埋在表格第三欄裡的一段字。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">上游一改，依賴自動標記 stale</h3>
            <p>「依賴」在這裡是真實的連結。上游決策改了，所有依賴它的卡片自動標記過時——像建置系統把下游全部標為需要重編，而不是等人想起來回頭翻。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI agent 能讀它，也能寫它</h3>
            <p>Claude Code 或 Cursor 透過 MCP 讀到每一筆決策的脈絡再動手，新的決定也寫回同一套結構。紀錄不再是沒人回看的檔案，而是每一輪都被讀取的上下文。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          範本告訴你要寫哪些欄位。它沒辦法告訴你，哪個欄位已經跟事實對不上了。
        </blockquote>
        <cite>欄位照抄。讓底層結構替你保鮮。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">常見問題。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '決策紀錄範本要包含哪些欄位？', a: '至少八個：決策本身、當時脈絡、考慮過的選項、選擇理由、依賴的前提、目前狀態、負責人、日期。其中「理由」和「依賴」最常被省略，也最值錢——理由讓翻案有據可查，依賴讓過時可被發現。' },
            { q: '決策紀錄和 ADR（架構決策紀錄）有什麼差別？', a: 'ADR 是決策紀錄在軟體架構領域的特化版，格式幾乎相同（脈絡、決策、後果）。產品、營運、商務的決策一樣適用這套欄位——差別只在記錄的對象，不在方法。' },
            { q: '用 Notion 或 Google 文件做決策紀錄可以嗎？', a: '可以起步，多數團隊也是這樣開始的。問題出在維護：文件裡的「依賴」只是文字，上游變動不會通知下游，紀錄悄悄過時、沒人發現。工具能不能自動標記過時，是紀錄能不能撐過三個月的分水嶺。' },
            { q: 'Draftlize 的決策紀錄範本有什麼不同？', a: '同樣八個欄位，但每個欄位是一張可定位的結構化卡片：依賴是真實連結，上游決策一改、依賴它的卡片自動標記 stale；AI agent 透過 MCP 讀寫同一套結構。一鍵就能把這個範本開成專案，欄位預先建好。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <TemplateCta template="decision-log" locale="zhtw" />
    </MarketingShell>
  );
}
